import { Injectable, inject } from '@angular/core';
import { GeoJSONSource, Map } from 'maplibre-gl';
import { NavigationService } from '../core/navigation.service';
import { UtilsService } from '../utils/utils.service';
import { Route, NextTurn } from 'src/app/models/route';
import { Subscription } from 'rxjs';
import { MapSkin, SettingsService } from '../account/settings.service';
import { MapPaletteService } from './map-palette.service';

const TURN_CONTEXT_DISTANCE = 50; // meters before/after turn

const TURN_LINE_OUTLINE_LAYER = 'turn-instruction-outline';
const TURN_LINE_FILL_LAYER = 'turn-instruction-fill';
const TURN_SOURCE = 'turn-instruction-source';
const TURN_ARROW_HEAD_SRC = 'turn-arrow-head-source';
const TURN_ARROW_HEAD_LAYER = 'turn-arrow-head-layer';
const TURN_ARROW_HEAD_IMG = 'turn-arrow-head-image';

@Injectable({
  providedIn: 'root'
})
export class MapTurnDrawerService {

  private mapInstance: Map | null = null;
  private routeData: Route | null = null;
  private lastTurnRouteId: number | null = null;
  private subscriptions: Subscription[] = [];

  private readonly navigationService = inject(NavigationService);
  private readonly utilsService = inject(UtilsService);
  private readonly settingsService = inject(SettingsService);
  private readonly mapPaletteService = inject(MapPaletteService);

  public setMapInstance(map: Map): void {
    this.mapInstance = map;
  }

  public setRouteData(routeData: Route): void {
    this.routeData = routeData;
    this.lastTurnRouteId = null;
  }

  public startListening(): void {
    this.stopListening();

    this.subscriptions.push(
      this.navigationService.ObservableNextTurn.subscribe((resp: NextTurn | null) => {
        if (!resp || !this.routeData?.RoutePlan) return;
        if (resp.NextTurnInstruction.RoutePointIdxStart <= 0) return;
        if (resp.NextTurnInstruction.RoutePointIdxStart === this.lastTurnRouteId) return;

        if (resp.NextTurnInstruction.Id === 'ARRIVE' || resp.NextTurnInstruction.Id === 'ARRIVE_TO_WAYPOINT') {
          this.lastTurnRouteId = resp.NextTurnInstruction.Idx;
          this.clearTurnLayers();
          return;
        }

        this.lastTurnRouteId = resp.NextTurnInstruction.RoutePointIdxStart;
        this.drawTurn(resp);
      })
    );
  }

  public stopListening(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
  }

  public clear(): void {
    this.stopListening();
    this.clearTurnLayers();
    this.routeData = null;
    this.lastTurnRouteId = null;
  }

  private drawTurn(resp: NextTurn): void {
    if (!this.mapInstance || !this.routeData) return;

    const turnPoints = this.computeTurnPoints(resp);
    if (turnPoints.length < 2) return;

    // Build GeoJSON for turn instruction line
    const lineGeoJson: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: turnPoints
      }
    };

    this.ensureTurnLineLayers();

    // Update source data
    const source = this.mapInstance.getSource(TURN_SOURCE) as GeoJSONSource;
    if (source) {
      source.setData(lineGeoJson);
    }

    this.drawTurnArrow(turnPoints);
  }

  private computeTurnPoints(resp: NextTurn): number[][] {
    const rp = this.routeData!.RoutePoints;
    const { turnStart, turnEnd } = this.getTurnRange(resp);

    const turnPoints: number[][] = [];

    // Add turn center point
    turnPoints.push([rp[turnStart].Location.lng, rp[turnStart].Location.lat]);

    // Add points before turn (50m context)
    this.prependContextBeforeTurn(turnPoints, turnStart);

    // Add points after turn
    for (let i = turnStart + 1; i <= turnEnd; i++) {
      if (i < rp.length) {
        turnPoints.push([rp[i].Location.lng, rp[i].Location.lat]);
      }
    }

    // Add 50m context after turn
    this.appendContextAfterTurn(turnPoints, turnEnd);

    return turnPoints;
  }

  /** Route point range of the next turn, widened over roundabouts and combined turns */
  private getTurnRange(resp: NextTurn): { turnStart: number; turnEnd: number } {
    const turns = this.routeData!.Turns;
    const idx = resp.NextTurnInstruction.Idx;

    let turnStart = this.lastTurnRouteId!;
    let turnEnd = resp.NextTurnInstruction.RoutePointIdxEnd;

    if (!turns.length) {
      return { turnStart, turnEnd };
    }

    // Roundabout handling
    if (turns[idx].TypeIndex === 94) {
      turnEnd = turns[idx + 1].RoutePointIdxStart;
    }
    if (turns[idx].TypeIndex === 95) {
      turnStart = turns[idx - 1].RoutePointIdxStart;
    }

    // Combined turns (before)
    let combinedTurnIdx = idx;
    while (combinedTurnIdx - 1 >= 0 && this.isCombinedTurn(turns[combinedTurnIdx - 1])) {
      combinedTurnIdx--;
      turnStart = turns[combinedTurnIdx].RoutePointIdxStart;
    }

    // Combined turns (after)
    combinedTurnIdx = idx;
    while (combinedTurnIdx + 1 < turns.length && this.isCombinedTurn(turns[combinedTurnIdx])) {
      combinedTurnIdx++;
      turnEnd = turns[combinedTurnIdx].RoutePointIdxStart;
    }

    return { turnStart, turnEnd };
  }

  private isCombinedTurn(turn: { TypeIndex: number }): boolean {
    return turn.TypeIndex >= 12 && turn.TypeIndex <= 92;
  }

  /** Prepends route points up to 50m before the turn, the first one interpolated to exactly 50m */
  private prependContextBeforeTurn(turnPoints: number[][], turnStart: number): void {
    const rp = this.routeData!.RoutePoints;
    let remainingDist = TURN_CONTEXT_DISTANCE;
    let pointIdx = turnStart;

    while (remainingDist > 0 && pointIdx > 0) {
      remainingDist -= this.utilsService.distanceBetweenCoordinates(
        [rp[pointIdx].Location.lat, rp[pointIdx].Location.lng],
        [rp[pointIdx - 1].Location.lat, rp[pointIdx - 1].Location.lng]
      );
      pointIdx--;
      turnPoints.unshift([rp[pointIdx].Location.lng, rp[pointIdx].Location.lat]);
    }

    // Interpolate start point to exactly 50m
    if (remainingDist < 0 && turnPoints.length >= 2) {
      const lastDist = this.utilsService.distanceBetweenCoordinatesLonLat(turnPoints[0] as [number, number], turnPoints[1] as [number, number]);
      const alpha = (lastDist + remainingDist) / lastDist;
      const lon = this.utilsService.Lerpd(turnPoints[0][0], turnPoints[1][0], 1 - alpha);
      const lat = this.utilsService.Lerpd(turnPoints[0][1], turnPoints[1][1], 1 - alpha);
      turnPoints[0] = [lon, lat];
    }
  }

  /** Appends route points up to 50m after the turn, the last one interpolated to exactly 50m */
  private appendContextAfterTurn(turnPoints: number[][], turnEnd: number): void {
    const rp = this.routeData!.RoutePoints;
    let pointIdx = turnEnd;
    let remainingDist = TURN_CONTEXT_DISTANCE;
    while (remainingDist > 0 && pointIdx < rp.length - 1) {
      remainingDist -= this.utilsService.distanceBetweenCoordinates(
        [rp[pointIdx].Location.lat, rp[pointIdx].Location.lng],
        [rp[pointIdx + 1].Location.lat, rp[pointIdx + 1].Location.lng]
      );
      pointIdx++;
      turnPoints.push([rp[pointIdx].Location.lng, rp[pointIdx].Location.lat]);
    }

    // Interpolate end point to exactly 50m
    if (remainingDist < 0 && turnPoints.length >= 2) {
      const prevPoint = turnPoints.at(-2) as [number, number];
      const lastPoint = turnPoints.at(-1) as [number, number];
      const lastDist = this.utilsService.distanceBetweenCoordinatesLonLat(prevPoint, lastPoint);
      const alpha = (lastDist + remainingDist) / lastDist;
      const lon = this.utilsService.Lerpd(prevPoint[0], lastPoint[0], alpha);
      const lat = this.utilsService.Lerpd(prevPoint[1], lastPoint[1], alpha);
      turnPoints[turnPoints.length - 1] = [lon, lat];
    }
  }

  private ensureTurnLineLayers(): void {
    if (!this.mapInstance) return;

    if (!this.mapInstance.getSource(TURN_SOURCE)) {
      this.mapInstance.addSource(TURN_SOURCE, {
        type: 'geojson',
        data: { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [] } }
      });
    }

    if (!this.mapInstance.getLayer(TURN_LINE_OUTLINE_LAYER)) {
      this.mapInstance.addLayer({
        id: TURN_LINE_OUTLINE_LAYER,
        type: 'line',
        source: TURN_SOURCE,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': this.mapPaletteService.accent('rgb(24, 172, 233)'),
          'line-width': 16
        }
      }, this.mapInstance.getLayer('cursor-layer') ? 'cursor-layer' : undefined);
    }

    if (!this.mapInstance.getLayer(TURN_LINE_FILL_LAYER)) {
      this.mapInstance.addLayer({
        id: TURN_LINE_FILL_LAYER,
        type: 'line',
        source: TURN_SOURCE,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': this.mapPaletteService.surface(this.settingsService.getMap().Skin == MapSkin.Dark ? "#CFD9E4" : "#ffffff"),
          'line-width': 10
        }
      }, this.mapInstance.getLayer('cursor-layer') ? 'cursor-layer' : undefined);
    }
  }

  private drawTurnArrow(turnPoints: number[][]): void {
    if (!this.mapInstance || turnPoints.length < 2) return;

    const [px, py] = turnPoints.at(-2)!;
    const [lx, ly] = turnPoints.at(-1)!;
    const bearing = this.utilsService.calculateBearing(py, px, ly, lx);

    const pointFeature: GeoJSON.Feature<GeoJSON.Point> = {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [lx, ly] },
      properties: { bearing }
    };

    this.ensureArrowLayers();

    const source = this.mapInstance.getSource(TURN_ARROW_HEAD_SRC) as GeoJSONSource;
    if (source) {
      source.setData(pointFeature);
    }
  }

  private makeNotchedArrowHead(color: string, size = 64): ImageData {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d')!;
    ctx.clearRect(0, 0, size, size);

    const w = size, h = size;
    const notch = size * 0.2;
    const notchHeight = size * 0.1;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(w / 2, 0);              // tip
    ctx.lineTo(w, h);                   // bottom-right corner
    ctx.lineTo(w / 2 + notch, h);
    ctx.lineTo(w / 2 + notch, h - notchHeight);
    ctx.lineTo(w / 2, h - notchHeight); // notch center
    ctx.lineTo(w / 2 - notch, h - notchHeight);
    ctx.lineTo(w / 2 - notch, h);
    ctx.lineTo(0, h);                   // bottom-left corner
    ctx.closePath();
    ctx.fill();

    return ctx.getImageData(0, 0, size, size);
  }

  private makeSolidArrowHead(color: string, size = 64): ImageData {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d')!;
    ctx.clearRect(0, 0, size, size);

    const w = size, h = size;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(w / 2, 0);  // tip
    ctx.lineTo(w, h);       // bottom-right
    ctx.lineTo(0, h);       // bottom-left
    ctx.closePath();
    ctx.fill();

    return ctx.getImageData(0, 0, size, size);
  }

  private ensureArrowLayers(): void {
    if (!this.mapInstance) return;

    // Register arrow images once
    const blueImgId = TURN_ARROW_HEAD_IMG + '-blue';
    const whiteImgId = TURN_ARROW_HEAD_IMG + '-white';

    if (!this.mapInstance.hasImage(blueImgId)) {
      this.mapInstance.addImage(blueImgId, this.makeNotchedArrowHead(this.mapPaletteService.accent('rgb(24, 172, 233)')));
    }
    if (!this.mapInstance.hasImage(whiteImgId)) {
      const arrowColor = this.mapPaletteService.surface(this.settingsService.getMap().Skin == MapSkin.Dark ? "#CFD9E4" : "#ffffff");
      this.mapInstance.addImage(whiteImgId, this.makeSolidArrowHead(arrowColor));
    }

    // Point source for arrow head position
    if (!this.mapInstance.getSource(TURN_ARROW_HEAD_SRC)) {
      this.mapInstance.addSource(TURN_ARROW_HEAD_SRC, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
    }

    const beforeLayer = this.mapInstance.getLayer('cursor-layer') ? 'cursor-layer' : undefined;
    const offsetY = 20;

    // Blue (outer) arrow head — sized to always cover the outline line width
    if (!this.mapInstance.getLayer(TURN_ARROW_HEAD_LAYER + '-blue')) {
      this.mapInstance.addLayer({
        id: TURN_ARROW_HEAD_LAYER + '-blue',
        type: 'symbol',
        source: TURN_ARROW_HEAD_SRC,
        layout: {
          'icon-image': blueImgId,
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'bottom',
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            12, 0.3,
            14, 0.4,
            16, 0.55,
            18, 0.75
          ],
          'icon-offset': [0, offsetY]
        }
      }, beforeLayer);
    }

    // White (inner) arrow head — sized to always cover the fill line width
    if (!this.mapInstance.getLayer(TURN_ARROW_HEAD_LAYER + '-white')) {
      this.mapInstance.addLayer({
        id: TURN_ARROW_HEAD_LAYER + '-white',
        type: 'symbol',
        source: TURN_ARROW_HEAD_SRC,
        layout: {
          'icon-image': whiteImgId,
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'bottom',
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            12, 0.2,
            14, 0.3,
            16, 0.4,
            18, 0.6
          ],
          'icon-offset': [0, offsetY]
        }
      }, beforeLayer);
    }
  }

  private clearTurnLayers(): void {
    if (!this.mapInstance) return;

    if (this.mapInstance.getSource(TURN_SOURCE)) {
      (this.mapInstance.getSource(TURN_SOURCE) as GeoJSONSource).setData(
        { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [] } }
      );
    }
    this.clearArrowLayer();
  }

  private clearArrowLayer(): void {
    if (!this.mapInstance) return;

    // Clear arrow head layers
    [TURN_ARROW_HEAD_LAYER + '-blue', TURN_ARROW_HEAD_LAYER + '-white'].forEach(id => {
      if (this.mapInstance!.getLayer(id)) this.mapInstance!.removeLayer(id);
    });

    // Clear arrow head source
    if (this.mapInstance.getSource(TURN_ARROW_HEAD_SRC)) {
      this.mapInstance.removeSource(TURN_ARROW_HEAD_SRC);
    }

    // Remove arrow head images
    [TURN_ARROW_HEAD_IMG + '-blue', TURN_ARROW_HEAD_IMG + '-white'].forEach(id => {
      if (this.mapInstance!.hasImage(id)) this.mapInstance!.removeImage(id);
    });
  }

  /** Called when zoom changes — symbol layers auto-scale via icon-size interpolation */
  public onZoomChange(): void {
    // No-op: symbol layers handle zoom scaling natively
  }
}
